import { Modal } from 'antd';
import React from 'react';
import { useIsNarrow } from '@/hooks/useIsNarrow';
import styles from './index.module.less';

export type DetailSectionItem = {
  key: string;
  label: string;
  count?: number;
};

export type DetailFact = {
  label: string;
  value: React.ReactNode;
};

export function DetailModal({
  open,
  onCancel,
  title,
  width = 'min(1200px, calc(100vw - 48px))',
  bodyHeight = 'min(760px, calc(100dvh - 120px))',
  footer,
  children,
}: React.PropsWithChildren<{
  open: boolean;
  onCancel: () => void;
  title: string;
  width?: string;
  bodyHeight?: string;
  footer?: React.ReactNode;
}>) {
  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={title}
      footer={footer ?? null}
      width={width}
      centered
      destroyOnHidden
      keyboard
      closable={{ 'aria-label': '关闭' }}
      className={styles.modal}
      styles={{
        container: {
          padding: 0,
          overflow: 'hidden',
        },
        header: {
          padding: '16px 56px 0 16px',
        },
        body: {
          padding: 0,
          height: bodyHeight,
          overflow: 'hidden',
        },
      }}
    >
      {children}
    </Modal>
  );
}

const expandedShellStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateRows: 'auto minmax(0, 1fr)',
  gap: 12,
  height: '100%',
  minHeight: 0,
  padding: 16,
  boxSizing: 'border-box',
  overflow: 'hidden',
};

const expandedWorkspaceStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '176px minmax(0, 1fr)',
  gap: 16,
  minHeight: 0,
};

const expandedNavStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minHeight: 0,
  paddingBlock: 2,
  overflow: 'auto',
};

const expandedContentStyle: React.CSSProperties = {
  minWidth: 0,
  minHeight: 0,
  overflow: 'auto',
  padding: 4,
  margin: -4,
};

export function DetailInspector({
  header,
  sections,
  activeKey,
  onChange,
  ariaLabel = '详情分区',
  children,
}: React.PropsWithChildren<{
  header: React.ReactNode;
  sections: DetailSectionItem[];
  activeKey: string;
  onChange: (key: string) => void;
  ariaLabel?: string;
}>) {
  const compact = useIsNarrow(1000);
  return (
    <div className={styles.root}>
      <div
        className={styles.shell}
        style={
          compact ? { display: 'block', overflow: 'auto' } : expandedShellStyle
        }
      >
        <div className={styles.header}>{header}</div>
        <div
          className={styles.workspace}
          style={
            compact
              ? { display: 'block', overflow: 'visible' }
              : expandedWorkspaceStyle
          }
        >
          <nav
            className={styles.nav}
            aria-label={ariaLabel}
            style={
              compact
                ? {
                    flexDirection: 'row',
                    maxHeight: 160,
                    marginBottom: 12,
                    paddingBottom: 8,
                    overflowX: 'auto',
                    borderBottom: '1px solid var(--ant-color-border-secondary)',
                  }
                : expandedNavStyle
            }
          >
            {sections.map((section) => (
              <button
                key={section.key}
                type="button"
                className={[
                  styles.navButton,
                  activeKey === section.key ? styles.navButtonActive : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-current={activeKey === section.key ? 'page' : undefined}
                data-section={section.key}
                onClick={() => onChange(section.key)}
                style={
                  compact ? { width: 'auto', whiteSpace: 'nowrap' } : undefined
                }
              >
                <span>{section.label}</span>
                {typeof section.count === 'number' ? (
                  <span className={styles.navCount}>{section.count}</span>
                ) : null}
              </button>
            ))}
          </nav>
          <div
            className={styles.content}
            style={
              compact
                ? { height: 'auto', overflow: 'visible' }
                : expandedContentStyle
            }
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

export function DetailSummary({
  title,
  tags,
  context,
  facts,
  actions,
  highlight,
}: {
  title: React.ReactNode;
  tags?: React.ReactNode;
  context?: React.ReactNode;
  facts?: DetailFact[];
  actions?: React.ReactNode;
  highlight?: { label: string; value: React.ReactNode; unit?: string };
}) {
  const singleColumn = useIsNarrow(320);
  const twoColumns = useIsNarrow(900);
  const factColumns = singleColumn
    ? 'minmax(0, 1fr)'
    : twoColumns
      ? 'repeat(2, minmax(0, 1fr))'
      : 'repeat(4, minmax(0, 1fr))';
  return (
    <div className={styles.summary}>
      <div className={styles.summaryMain}>
        {tags ? <div className={styles.tags}>{tags}</div> : null}
        <div className={styles.titleRow}>
          <h2 className={styles.title}>{title}</h2>
          {actions ? <div className={styles.actions}>{actions}</div> : null}
        </div>
        {context ? <div className={styles.context}>{context}</div> : null}
        {facts?.length ? (
          <div
            className={styles.facts}
            style={{ gridTemplateColumns: factColumns }}
          >
            {facts.map((fact) => (
              <div className={styles.fact} key={fact.label}>
                <span className={styles.factLabel}>{fact.label}</span>
                <span className={styles.factValue}>{fact.value ?? '-'}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
      {highlight ? (
        <div className={styles.highlight}>
          <span className={styles.highlightLabel}>{highlight.label}</span>
          <div className={styles.highlightValueRow}>
            <span className={styles.highlightValue}>{highlight.value}</span>
            {highlight.unit ? (
              <span className={styles.highlightUnit}>{highlight.unit}</span>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function DetailSection({
  title,
  hint,
  extra,
  children,
}: React.PropsWithChildren<{
  title: string;
  hint?: React.ReactNode;
  extra?: React.ReactNode;
}>) {
  return (
    <section className={styles.section}>
      <header className={styles.sectionHeader}>
        <h3>{title}</h3>
        {hint || extra ? (
          <div className={styles.sectionExtra}>
            {hint ? <span className={styles.sectionHint}>{hint}</span> : null}
            {extra}
          </div>
        ) : null}
      </header>
      <div className={styles.sectionBody}>{children}</div>
    </section>
  );
}
